/* eslint-disable max-classes-per-file */
import { ComponentRef, Type } from "@angular/core";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import Point from "@arcgis/core/geometry/Point";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapView from "@arcgis/core/views/MapView";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { State } from "@arcgis/core/widgets/Sketch/types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { GeometryInformation } from "./components/widgets/edit-menu/models/edit-models";
import {
    IMapDrawLayer,
    IMapGeometry,
    IMapSettings,
    ISketchParams,
    SketchToolType
} from "./map.settings";

export class MapConstants {
    // eslint-disable-next-line sonarjs/public-static-readonly
    static validExtentSweden = [
        [
            200000,
            6000000
        ],
        [
            1070000,
            7700000
        ]
    ];
}

export interface IHitItem {
    layer: IMapDrawLayer;
    geometry: IMapGeometry;
    mouse: any;
    graphicslayer: any;
    graphic: any;
}

export interface IMapMouse {
    x: number;
    y: number;
    clientX: number;
    clientY: number;
    screenX: number;
    screenY: number;
    point?: any;
    coords?: any;
}

export class MapExtent {

    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    sr: any;

    constructor(minX: number, minY: number, maxX: number, maxY: number, sr?: any) {
        this.minX = minX;
        this.minY = minY;
        this.maxX = maxX;
        this.maxY = maxY;
        this.sr = sr;
    }
}

export class MapMouseData {

    static readonly Left = 0;

    static readonly Right = 2;
}

export class RightClickData {
    clicked: IMapGeometry;
    hitlist: GeometryInformation[];
}

export interface IArcGisPointerEvent {
    x: number;
    y: number;
    type: string;
    native: MouseEvent | PointerEvent;
}

export class ArcGisClickEvent implements IArcGisPointerEvent {
    mapPoint: Point;
    x: number;
    y: number;
    type: string;
    button: number;
    buttons: number; // 0 | 1 | 2
    stopPropagation: any; // () => void;
    timestamp: number;
    native: MouseEvent | PointerEvent;
}

export class ArcGisPointerMoveEvent implements IArcGisPointerEvent {
    pointerId: number;
    x: number;
    y: number;
    type: string;
    pointerType: string;
    button: number;
    buttons: number;
    stopPropagation: any;
    timestamp: number;
    native: MouseEvent | PointerEvent;
}

export class CoordsViewModel {
    isOpen: boolean;
    open$ = new BehaviorSubject<boolean>(false);
    x: number;
    y: number;
    navigateToX?: number;
    navigateToY?: number;
    isValid: boolean;
    toggleMenu(): void {
        this.isOpen = !this.isOpen;
        this.open$.next(this.isOpen);
    }
    canNavigate(): boolean {
        return !!this.navigateToX && !!this.navigateToY && !isNaN(this.navigateToX) && !isNaN(this.navigateToY);
    }
    getX(): string {
        return this.x?.toFixed(0) ?? "";
    }
    getY(): string {
        return this.y?.toFixed(0) ?? "";
    }
    setNavigateCoordinate(x: number, y: number): void {
        if (this.testXY(x, y)) {
            this.navigateToX = x;
            this.navigateToY = y;
            this.isValid = true;
        } else {
            this.isValid = false;
        }
    }

    updateNavigateCoordinate(): void {
        this.isValid = this.testXY(this.navigateToX, this.navigateToY);
    }

    private testXY(x?: number, y?: number): boolean {
        return ObjectUtils.hasValue(x) && !isNaN(x) && ObjectUtils.hasValue(y) && !isNaN(y) &&
            (x >= MapConstants.validExtentSweden[0][0] && x <= MapConstants.validExtentSweden[1][0] &&
                y >= MapConstants.validExtentSweden[0][1] && y <= MapConstants.validExtentSweden[1][1]);
    }
}

export interface IHighlightedGraphic {
    highlight: ResourceHandle;
    onRemove?: () => void;
}
export interface IMapSketch {
    onStateChange: Subject<string>;
    init(graphicsLayers: GraphicsLayer[], view: MapView): void;
    sketch(layer: IMapDrawLayer, tool: SketchToolType, onCreateCallback?: (g) => void): void;
    onAbort(callback?: () => void);
    edit(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void, disableMove?: boolean): void;
    add(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void): void;
    cut(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void): void;
    startSketch(params: ISketchParams, callback?: () => void): SketchViewModel;
    updateSketchWidget(layerId?: string): void;
    complete(): void;
    cancelSketch(layer?): void;
    sketchState: State;
}

export interface IMapComponent {
    isPopupBlocked(): boolean;
    allLayersLoaded: boolean;
    setMapHeight(height: string);
    setSubset(id: any): void;
    getSubset(): any;
    selectGeometries(layerid: string, ids: any[], signal?);
    deselectGeometries(layerid: string, ids: any[], signal?);
    getSelectedGeometryIds(layerid: string): any[];
    getGeometriesByGeometry(layerid: string, selector: IMapGeometry): IMapGeometry[];
    clearSelection();
    filterGeometries(layers: string[], ids: any[]);
    filterLayer(layerid: string, expression: string, sublayerids?: string[]);
    setGeometries(layerid, geometries: IMapGeometry[], goto?: boolean);
    addGeometries(layerId: string, geometries: IMapGeometry[], goto?: boolean);
    clearGeometries(layerId: string);
    preview(geometries: IMapGeometry[], color?: any, clearFirst?: boolean, timeout?: number, goto?: boolean);
    updatePreview(geometries: IMapGeometry[], insertIfNeeded?: boolean, color?: any);
    deletePreview(geometryIds: string[]);
    clearPreview();
    refreshSubComponents(layerId: string);
    refreshGeometries(layerid: string);
    refreshGraphics(layerid: string, geometries: IMapGeometry[]);
    gotoExtent(extent: MapExtent, smooth?: boolean);
    gotoExtentDeprecated(xmin: number, ymin: number, xmax: number, ymax: number);
    getExtent(): MapExtent;
    /**
     * Go to all graphics in layers if exists, or do nothing.
     *
     * @param {string} layerids
     */
    gotoAll(layerids?: string[]);
    /**
     * Go to all graphics in layers, or pan out to default extent.
     *
     * @param {string} layerids
     */
    gotoSelectedOrDefault();
    /**
     * Go to SELECTED geometries in all layers, or pan out to default extent.
     */
    gotoAllOrDefault(layerids?: string[]);
    /**
     * Go to geometry part of graphicslayer
     */
    gotoGeometry(geometry: IMapGeometry, layer: string);
    /**
     * Go to geometry not part of any graphicslayer
     */
    gotoGeometries(geometries: IMapGeometry[], smooth?: boolean);
    gotoCoordinate(swerefX: number, swerefY: number, scale?: number);
    highlightGeometries(layerid: string, geometries: IMapGeometry[]);
    closePopups(): void;
    showPopup<T>(innerComponent: Type<T>, mouseEvent: MouseEvent, data: any, targetGeometry?: IMapGeometry): Observable<ComponentRef<T>>;
    showPopupOnGeometry<T>(innerComponent: Type<T>, geometry: IMapGeometry, data?: any): Observable<ComponentRef<T>>;
    updatePopupPosition();
    openMenu(menuid: string);
    clearAllHighlights();
    isWidgetsActive(widgets: string[]): boolean;
    openWidget(widgetId: string): void;
    closeWidget(widgetId: string): void;
    closeWidgets();
    reorderPolygonsBySize(layerid);
    createDrawLayer(layerid?: string): any;
    removeDrawLayer(layerid: string);
    removeMediaLayer(layerid: string);
    hideDrawLayer(layerid: string);
    showDrawLayer(layerid);
    showMediaLayer(layerid: string);
    hideMediaLayer(layerid);
    hideFeatureLayer(layerid);
    showFeatureLayer(layerid): Observable<boolean>;
    isLayerVisible(layerid: string): boolean;
    setLayerFocus(layerids: string[]): void;
    clearLayerFocus(): void;
    toggleFullscreen();
    getZoomLevel(): number;
    getScaleLevel(): number;
    updateGeometry(geometry: IMapGeometry, layerId?: string, remove?: boolean);
    updateGeometryAttributes(raskGeometry: IMapGeometry);
    getSketch(): IMapSketch;
    updateCoordsWidget(event: IMapMouse): void;
    getMapSettings(): IMapSettings;
    setClass(classes: string[]): void;
    clearClass(): void;
}

export class LayerType {
    static readonly mapImage = "map-image";
    static readonly graphics = "graphics";
    static readonly wms = "wms";
}

export class GeometryType {
    // eslint-disable-next-line sonarjs/public-static-readonly
    static point = "point";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static multipoint = "multipoint";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static polyline = "polyline";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static polygon = "polygon";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static multipolygon = "multipolygon";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static extent = "extent";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static mesh = "mesh";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static linestring = "linestring";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static multilinestring = "multilinestring";
}
